"use client"

import React, { useCallback, useLayoutEffect, useRef, useState } from "react"
import ReactDOM from "react-dom"
import type { OBTaskActor } from "@/types/workflow"
import { useTranslation } from "@/hooks/use-translation"
import { getOrgTypeStyle } from "./org-type-colors"

interface AvatarClusterProps {
  actors: OBTaskActor[]
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function Avatar({ actor, label, size = 24 }: { actor: OBTaskActor; label: string; size?: number }) {
  const style = getOrgTypeStyle(actor.actorType)
  return (
    <span
      title={`${actor.name} (${label})`}
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        border: `2px solid ${style.color}`,
        backgroundColor: "#fff",
        color: style.color,
        fontSize: 9,
        fontWeight: 700,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        letterSpacing: 0,
        lineHeight: 1,
        marginLeft: -4,
      }}
    >
      {initials(actor.name)}
    </span>
  )
}

function OverflowChip({ count, size = 24 }: { count: number; size?: number }) {
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        border: "2px solid #CBD5E1",
        backgroundColor: "#F1F5F9",
        color: "#64748B",
        fontSize: 9,
        fontWeight: 700,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        marginLeft: -4,
      }}
    >
      +{count}
    </span>
  )
}

interface TooltipPos {
  left: number
  top?: number
  bottom?: number
}

function AssigneeTooltip({
  actors,
  pos,
  onMouseEnter,
  onMouseLeave,
}: {
  actors: OBTaskActor[]
  pos: TooltipPos
  onMouseEnter: () => void
  onMouseLeave: () => void
}) {
  const { t } = useTranslation()

  const content = (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: "fixed",
        left: pos.left,
        top: pos.top,
        bottom: pos.bottom,
        zIndex: 9999,
        minWidth: 270,
        backgroundColor: "#fff",
        border: "1px solid #E2E8F0",
        borderRadius: 8,
        boxShadow: "0 4px 20px rgba(0,0,0,0.14)",
        padding: "8px 0",
        pointerEvents: "auto",
      }}
    >
      {actors.length === 0 ? (
        <p style={{ padding: "4px 12px", fontSize: 12, color: "#94A3B8" }}>{t('rppNoAssignees')}</p>
      ) : (
        <>
          <p style={{ padding: "2px 12px 6px", fontSize: 10, fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {t('rppAssigneesCount', { count: actors.length })}
          </p>
          {actors.map((actor, i) => {
            const orgStyle = getOrgTypeStyle(actor.actorType)
            const label = orgStyle.fallbackLabel ?? t(orgStyle.labelKey)
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "5px 12px", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: orgStyle.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: "#1E293B", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {actor.name}
                  </span>
                </div>
                <span style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: orgStyle.color,
                  border: `1px solid ${orgStyle.color}`,
                  borderRadius: 4,
                  padding: "1px 6px",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}>
                  {label}
                </span>
              </div>
            )
          })}
        </>
      )}
    </div>
  )

  if (typeof document === "undefined") return null
  return ReactDOM.createPortal(content, document.body)
}

const MAX_VISIBLE = 3
const SHOW_DELAY_MS = 120
const HIDE_DELAY_MS = 80

export function AvatarCluster({ actors }: AvatarClusterProps) {
  const { t } = useTranslation()
  const [pos, setPos] = useState<TooltipPos | null>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const computePos = useCallback((): TooltipPos | null => {
    if (!anchorRef.current) return null
    const rect = anchorRef.current.getBoundingClientRect()
    const tooltipHeight = 180
    const above = rect.bottom + tooltipHeight > window.innerHeight
    // Align right edge of tooltip with right edge of cluster
    const left = Math.max(8, rect.right - 270)
    if (above) {
      return { left, bottom: window.innerHeight - rect.top + 6 }
    }
    return { left, top: rect.bottom + 6 }
  }, [])

  const scheduleShow = useCallback(() => {
    if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null }
    if (pos) return // already visible
    showTimerRef.current = setTimeout(() => {
      setPos(computePos())
    }, SHOW_DELAY_MS)
  }, [computePos, pos])

  const scheduleHide = useCallback(() => {
    if (showTimerRef.current) { clearTimeout(showTimerRef.current); showTimerRef.current = null }
    hideTimerRef.current = setTimeout(() => {
      setPos(null)
    }, HIDE_DELAY_MS)
  }, [])

  const cancelHide = useCallback(() => {
    if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null }
  }, [])

  const visible = actors.slice(0, MAX_VISIBLE)
  const overflow = actors.length - MAX_VISIBLE

  return (
    <div
      ref={anchorRef}
      onMouseEnter={scheduleShow}
      onMouseLeave={scheduleHide}
      style={{ display: "inline-flex", alignItems: "center", paddingLeft: 4 }}
    >
      {visible.map((actor, i) => {
        const orgStyle = getOrgTypeStyle(actor.actorType)
        const label = orgStyle.fallbackLabel ?? t(orgStyle.labelKey)
        return <Avatar key={i} actor={actor} label={label} />
      })}
      {overflow > 0 && <OverflowChip count={overflow} />}
      {pos && (
        <AssigneeTooltip
          actors={actors}
          pos={pos}
          onMouseEnter={cancelHide}
          onMouseLeave={scheduleHide}
        />
      )}
    </div>
  )
}
